/* eslint-disable @typescript-eslint/no-floating-promises */

'use client'

import React, { useEffect, useState } from 'react'
import Image from 'next/image'
import StyledComponentsRegistry from '@configs/registry'
import assetLoader from '@utils/internalImageLoader'
import isMobile from 'ismobilejs'
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button'
import { ThemeProvider } from 'styled-components'
import { AckoMainTheme } from '@acko-tech/building-blocks.ui.theme'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { autoService } from '@services/autoService'
import Cookies from 'js-cookie'
import { validateVehicleNumber } from '@components/MainWidget/FastagWidget/helper'
import { INPUT_ICON, INPUT_ICON_LOGO } from '@components/MainWidget/FastagWidget/constants'
import PropertyControlledComponent from '@components/PropertyControlledComponent/PropertyControlledComponent'
import CreateLead from '@services/createLead'
import styles from './widgetInput.module.scss'
import LoginModal from './loginModal'
import { CLOSE_BTN_ALT, CLOSE_BTN_ICON, DASH_FALLBACK, INPUT_ERROR_NOT_VALID } from './commonConstants'
import { commonWidgetConfig, vehicleTypeConfig } from './commonWidgetConfig'
import { SEGMENT } from './widgetSegments'
import { WidgetInputProps } from './widgetInputInterface'

export const WidgetInput = ({
  segmentEventCommonProperties,
  widgetHeading,
  widgetDefaultHeading,
  inputPlaceHolder,
  widgetCTA,
  widgetCTAFallback,
  regexPatternsData,
  setWidgetModal,
  setVehicleData,
  vehicleData,
  setRegNo,
  regNo,
  widgetType,
  isAckoDrive,
  setOnWidgetFloaterClick,
  show,
  phone,
  setPhone,
  userData,
  setUserLoggedIn,
  isLoggedIn,
}: WidgetInputProps) => {
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState('')
  const [isLoginModal, setIsLoginModal] = useState(false)

  const handleError = () => {
    setError(INPUT_ERROR_NOT_VALID)
    setIsFetching(false)
  }
  const fetchVehicleData = async () => {
    setIsFetching(true)
    const response = await autoService(regNo)
    if (!response) {
      handleError()
      return
    }
    setError('')
    setVehicleData(response)
    setIsFetching(false)
    handleModalDisplay()
  }

  const onFormSubmit = async e => {
    e.preventDefault()
    const vehicleError = validateVehicleNumber(regNo, regexPatternsData)
    vehicleError ? setError(vehicleError) : fetchVehicleData()
}

  // Initial auth check on component mount for Acko Drive
  useEffect(() => {
    if (isAckoDrive) {
      const userId = Cookies.get('user_id')
      if (userId) {
        setUserLoggedIn(true)
      }
    }
  }, [])

  useEffect(() => {
    setPhone(userData?.phone || DASH_FALLBACK)
  }, [userData])

  useEffect(() => {
    if (isLoggedIn && Object.keys(vehicleData ?? {}).length !== 0) {
      setWidgetModal(true)
    }
  }, [isLoggedIn])

  useEffect(() => {
    if (isLoginModal) {
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = 'scroll'
    }
  }, [isLoginModal])

  const handleModalDisplay = () => {
    trackEvent(SEGMENT.TAP_BTN, {
      ...segmentEventCommonProperties,
      product: commonWidgetConfig.product[widgetType],
      phone: phone || userData?.phone,
      registration_number: regNo,
    })
    if (!isLoggedIn) {
      setIsLoginModal(true)
    } else {
      setWidgetModal(true)
      CreateLead({
        registrationNumber: regNo,
        phoneNo: phone || DASH_FALLBACK,
        productKey: vehicleTypeConfig.PRIVATE_CAR || vehicleTypeConfig.DEFAULT,
        sourceName: commonWidgetConfig.source[widgetType],
        apiKey: commonWidgetConfig.apiKey[widgetType]
      })
    }
  }

  return (
    <ThemeProvider theme={AckoMainTheme}>
      <div>
        {isMobile()?.phone && isAckoDrive ? (
          <span onClick={() => setOnWidgetFloaterClick(false)}>
            <Image src={`${assetLoader()}${CLOSE_BTN_ICON}`} alt={CLOSE_BTN_ALT} width={20} height={20} />
          </span>
        ) : null}
        <form
          className={`${styles.widgetForm} ${isAckoDrive ? styles.inputBlowWidget : ''} ${
            isMobile()?.phone && isAckoDrive ? styles.isMobile : ''
          } ${show ? styles.isShow : ''}`}
          onSubmit={e => {
            e.preventDefault()
            onFormSubmit(e)
          }}>
          <label htmlFor="widgetInput" className={styles.widgetHeading}>
            {widgetHeading || widgetDefaultHeading}
          </label>
          <div className={styles.InputBoxContainer}>
            <Image
              width={0}
              height={0}
              src={`${assetLoader()}${INPUT_ICON}`}
              className={styles.inputIcon}
              alt={INPUT_ICON_LOGO}
            />
            <input
              type="text"
              id="widgetInput"
              placeholder={inputPlaceHolder}
              onChange={e => setRegNo(e.target.value.toUpperCase())}
              autoComplete="off"
              value={regNo}
            />
          </div>
          {error && <div className={styles.inputError}>{error}</div>}
          {/* for carbon */}
          {isMobile()?.phone && show ? (
            <span className={styles.widgetDescription}>{commonWidgetConfig.widgetDescription[widgetType]}</span>
          ) : null}
          {/* for acko drive */}
          {isMobile()?.phone && isAckoDrive ? (
            <span className={styles.widgetDescription}>{commonWidgetConfig.widgetDescription[widgetType]}</span>
          ) : null}
          <StyledComponentsRegistry>
            <PrimaryButton loading={isFetching} className={styles.primaryButton}>
              {widgetCTA || widgetCTAFallback}
            </PrimaryButton>
          </StyledComponentsRegistry>
        </form>
        <PropertyControlledComponent fallback={null} controllerProperty={isLoginModal && !isLoggedIn}>
          <LoginModal
            segmentEventCommonProperties={segmentEventCommonProperties}
            regNo={regNo}
            setUserLoggedIn={setUserLoggedIn}
            setIsLoginModal={setIsLoginModal}
            widgetType={widgetType}
          />
        </PropertyControlledComponent>
      </div>
    </ThemeProvider>
  )
}
